import { useCallback, useEffect, useState } from 'react'

/**
 * Tracks which horizontal edges a scroll container is resting against, so a table can show a fade
 * only on the side that actually has content hidden. Both default to true, so a table that fits its
 * container never shows one.
 *
 * Attach the returned `ref` to the element with `overflow-x: auto`. `element` is that same node once
 * it has mounted, for callers that need to attach their own listeners to it.
 */
export const useHorizontalScrollEdges = () => {
  // A callback ref held in state, not a useRef: the scroll container mounts on a later render (the
  // table renders nothing until it has sections), and a ref object's `.current` quietly filling in
  // never re-runs an effect. Keeping the node in state makes "the element arrived" a dependency the
  // effects below — and the caller's — can actually observe.
  const [element, setElement] = useState<HTMLDivElement | null>(null)
  const ref = useCallback((node: HTMLDivElement | null) => setElement(node), [])
  const [edges, setEdges] = useState({ atLeft: true, atRight: true })

  const measure = useCallback(() => {
    if (!element) return

    const atLeft = element.scrollLeft <= 0
    // A pixel of slack: fractional layout widths leave scrollLeft + clientWidth a hair short of
    // scrollWidth at the far right, which would strand the fade permanently on.
    const atRight = element.scrollLeft + element.clientWidth >= element.scrollWidth - 1

    // Bail when nothing changed — this runs from a ResizeObserver, and setting state
    // unconditionally there re-renders on every observed frame.
    setEdges((previous) =>
      previous.atLeft === atLeft && previous.atRight === atRight ? previous : { atLeft, atRight },
    )
  }, [element])

  useEffect(() => {
    if (!element) return

    measure()

    element.addEventListener('scroll', measure, { passive: true })

    // The container can start or stop overflowing without a scroll ever firing — the window
    // resizes, or a property section expands and changes the layout.
    const observer = new ResizeObserver(measure)

    observer.observe(element)

    return () => {
      element.removeEventListener('scroll', measure)

      observer.disconnect()
    }
  }, [element, measure])

  return { ref, element, atLeft: edges.atLeft, atRight: edges.atRight, measure }
}
